import React, { FC, useState, useCallback, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Alert,
  Stack
} from '@mui/material'
import { GenNode } from '../../types/carbon/models/GenNode'

export interface BaseSelectionModalProps {
  open: boolean
  onClose: () => void
  onSave: (baseTypesToAdd: string[], baseTypesToRemove: string[]) => void
  selectedNode?: GenNode | null
  initialBaseTypes?: string[]
}

interface BaseType {
  id: string
  name: string
  description: string
}

const BASE_TYPES: BaseType[] = [
  {
    id: 'cuf',
    name: 'Unweighted Total',
    description: 'Unweighted Filtered (cuf) - Count of unweighted respondents'
  },
  {
    id: 'cwf', 
    name: 'Total',
    description: 'Weighted Filtered (cwf) - Count of weighted respondents'
  },
  {
    id: 'ebf',
    name: 'Effective Base',
    description: 'Effective Base Filtered (ebf) - Effective sample size'
  }
]

const BaseSelectionModal: FC<BaseSelectionModalProps> = ({
  open,
  onClose,
  onSave,
  selectedNode,
  initialBaseTypes = []
}) => {
  const [selectedBases, setSelectedBases] = useState<string[]>([])
  const [initialBases, setInitialBases] = useState<string[]>([])

  // Initialize selected bases when modal opens
  useEffect(() => {
    if (open) {
      setSelectedBases(initialBaseTypes)
      setInitialBases(initialBaseTypes)
    }
  }, [open, initialBaseTypes])

  const handleBaseToggle = useCallback((baseId: string) => {
    setSelectedBases(prev => {
      if (prev.includes(baseId)) {
        return prev.filter(id => id !== baseId)
      } else {
        return [...prev, baseId]
      }
    })
  }, [])

  const handleSelectAll = useCallback(() => {
    if (selectedBases.length === BASE_TYPES.length) {
      setSelectedBases([])
    } else {
      setSelectedBases(BASE_TYPES.map(base => base.id))
    }
  }, [selectedBases.length])

  const handleSave = useCallback(() => {
    // Calculate which bases to add and remove
    const baseTypesToAdd = selectedBases.filter(base => !initialBases.includes(base))
    const baseTypesToRemove = initialBases.filter(base => !selectedBases.includes(base))

    onSave(baseTypesToAdd, baseTypesToRemove)
  }, [selectedBases, initialBases, onSave])

  const handleCancel = useCallback(() => {
    onClose()
  }, [onClose])

  if (!selectedNode) {
    return null
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '400px' }
      }}
    >
      <DialogTitle>
        Add/Remove Bases for {selectedNode.value1 || 'Codeframe'}
      </DialogTitle>
      
      <DialogContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <Alert severity="info">
          Bases show counts of respondents for each column. Select which types of
          bases to add to this variable.
        </Alert>

        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Available Base Types
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Button 
              variant="outlined" 
              size="small"
              onClick={handleSelectAll}
              sx={{ mb: 2 }}
            >
              {selectedBases.length === BASE_TYPES.length ? 'DESELECT ALL' : 'SELECT ALL'}
            </Button>
          </Box>

          <Stack spacing={2}>
            {BASE_TYPES.map((baseType) => (
              <Box 
                key={baseType.id}
                sx={{ 
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedBases.includes(baseType.id)}
                      onChange={() => handleBaseToggle(baseType.id)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle2" component="div">
                        {baseType.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {baseType.description}
                      </Typography>
                    </Box>
                  }
                  sx={{ 
                    alignItems: 'flex-start',
                    '& .MuiFormControlLabel-label': { mt: -0.5 }
                  }}
                />
              </Box>
            ))}
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleCancel} variant="outlined">
          CANCEL
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
        >
          SAVE
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default BaseSelectionModal